import React, { useEffect, useMemo, useState } from 'react';
import moment, { Moment } from 'moment-timezone';

import ExpandableSelector from 'components/UI/V3/ExpandableSelector';
import { DatePicker } from 'components/UI/V3/DatePicker/DatePicker';
import { TimeSelector } from 'components/UI/V3/Forms/TimeSelector/TimeSelector';
import { useTimeZone } from 'hooks';

import styles from './MembershipsTimeSection.module.scss';

type TimeOption = {
    id: string;
    label: string;
};

export interface MembershipsTimeSectionProps {
    from?: Moment;
    to?: Moment;
    dateSelectorId: string;
    onChange?: (value: { from: Moment; to: Moment }) => void;
}

export const MembershipsTimeSection: React.FC<MembershipsTimeSectionProps> = ({
    from: fromProp,
    to: toProp,
    dateSelectorId,
    onChange
}) => {
    const timeZone = useTimeZone();

    const roundUpToNext15Minutes = (value: Moment): Moment => {
        const rounded = value.clone().second(0).millisecond(0);
        const remainder = rounded.minutes() % 15;
        if (remainder !== 0) {
            rounded.add(15 - remainder, 'minutes');
        }
        return rounded;
    };

    const getNext15MinuteInterval = (): Moment => {
        return roundUpToNext15Minutes(moment().tz(timeZone));
    };

    const getInitialTimes = (): { from: Moment; to: Moment } => {
        if (fromProp && fromProp.isValid() && toProp && toProp.isValid()) {
            return {
                from: fromProp.clone().tz(timeZone),
                to: toProp.clone().tz(timeZone)
            };
        }

        const defaultFrom = getNext15MinuteInterval();
        const defaultTo = defaultFrom.clone().add(15, 'minutes');

        return { from: defaultFrom, to: defaultTo };
    };

    const [from, setFrom] = useState<Moment>(() => getInitialTimes().from);
    const [to, setTo] = useState<Moment>(() => getInitialTimes().to);

    useEffect(() => {
        if (fromProp && fromProp.isValid() && toProp && toProp.isValid()) {
            setFrom(fromProp.clone().tz(timeZone));
            setTo(toProp.clone().tz(timeZone));
        }
    }, [fromProp, toProp, timeZone]);

    const date = useMemo(() => {
        if (from && from.isValid()) {
            return from.clone().tz(timeZone).startOf('day');
        }
        return moment().tz(timeZone).startOf('day');
    }, [from, timeZone]);

    const [isDateExpanded, setIsDateExpanded] = useState(false);

    const timeOptions: Array<TimeOption> = useMemo(() => {
        const options: TimeOption[] = [];
        const base = moment().tz(timeZone).startOf('day');

        for (let i = 0; i < 96; i++) {
            const time = base.clone().add(i * 15, 'minutes');
            options.push({
                id: time.format('hh:mm A'),
                label: time.format('h:mma')
            });
        }

        return options;
    }, [timeZone]);

    const normalizedFrom = useMemo(() => {
        if (!from || !from.isValid()) return from;
        const fromInTimezone = from.clone().tz(timeZone);
        const minutes = fromInTimezone.minute();
        const remainder = minutes % 15;
        if (remainder === 0) return fromInTimezone;
        return fromInTimezone
            .clone()
            .minute(minutes - remainder)
            .second(0)
            .millisecond(0);
    }, [from, timeZone]);

    const normalizedTo = useMemo(() => {
        if (!to || !to.isValid()) return to;
        const toInTimezone = to.clone().tz(timeZone);
        const minutes = toInTimezone.minute();
        const remainder = minutes % 15;
        if (remainder === 0) return toInTimezone;
        return toInTimezone
            .clone()
            .minute(minutes - remainder)
            .second(0)
            .millisecond(0);
    }, [to, timeZone]);

    const emitChange = (nextFrom: Moment, nextTo: Moment) => {
        const fromInTimezone = nextFrom.clone().tz(timeZone);
        const toInTimezone = nextTo.clone().tz(timeZone);
        onChange?.({
            from: fromInTimezone,
            to: toInTimezone
        });
    };

    const handleDateChange = (newDate: Moment | null) => {
        if (!newDate) return;

        const dateInTimezone = newDate.clone().tz(timeZone, true);
        const nextFrom = dateInTimezone.clone().hour(from.hour()).minute(from.minute()).second(0).millisecond(0);
        const nextTo = dateInTimezone.clone().hour(to.hour()).minute(to.minute()).second(0).millisecond(0);

        setFrom(nextFrom);
        setTo(nextTo);
        emitChange(nextFrom, nextTo);
        setIsDateExpanded(false);
    };

    const handleFromChange = (id: string) => {
        const nextFrom = moment.tz(`${date.format('YYYY-MM-DD')} ${id}`, 'YYYY-MM-DD hh:mm A', timeZone);
        const currentTo = to.clone().tz(timeZone);

        let nextTo = currentTo;
        if (nextFrom.isSameOrAfter(currentTo)) {
            nextTo = nextFrom.clone().add(15, 'minutes');
            setTo(nextTo);
        }

        setFrom(nextFrom);
        emitChange(nextFrom, nextTo);
    };

    const handleToChange = (id: string) => {
        const nextTo = moment.tz(`${date.format('YYYY-MM-DD')} ${id}`, 'YYYY-MM-DD hh:mm A', timeZone);
        const currentFrom = from.clone().tz(timeZone);

        let adjustedTo = nextTo;
        if (nextTo.isSameOrBefore(currentFrom)) {
            adjustedTo = currentFrom.clone().add(15, 'minutes');
            setTo(adjustedTo);
        } else {
            setTo(nextTo);
        }

        emitChange(currentFrom, adjustedTo);
    };

    const selectedFrom = useMemo(() => {
        if (!normalizedFrom || !normalizedFrom.isValid()) return undefined;
        const formattedTime = normalizedFrom.format('hh:mm A');
        return timeOptions.find(opt => opt.id === formattedTime);
    }, [normalizedFrom, timeOptions]);

    const selectedTo = useMemo(() => {
        if (!normalizedTo || !normalizedTo.isValid()) return undefined;
        const formattedTime = normalizedTo.format('hh:mm A');
        return timeOptions.find(opt => opt.id === formattedTime);
    }, [normalizedTo, timeOptions]);

    return (
        <div className={styles.container}>
            <ExpandableSelector
                id={dateSelectorId}
                title="Date"
                label={date.format('dddd, DD MMM YYYY')}
                expanded={isDateExpanded}
                onExpand={() => setIsDateExpanded(prev => !prev)}
                onClickAway={() => setIsDateExpanded(false)}
                dropdownAlign="left"
                childrenClassName={styles.dateDropdown}
            >
                <DatePicker
                    timeZone={timeZone}
                    value={date}
                    onChange={handleDateChange}
                />
            </ExpandableSelector>

            <div className={styles.timeSection}>
                <div className={styles.timeLabel}>Start Time</div>

                <div className={styles.timeRow}>
                    <TimeSelector
                        value={selectedFrom}
                        options={timeOptions}
                        onChange={handleFromChange}
                    />

                    <div className={styles.to}>TO</div>

                    <TimeSelector
                        value={selectedTo}
                        options={timeOptions}
                        onChange={handleToChange}
                    />
                </div>
            </div>
        </div>
    );
};

export default MembershipsTimeSection;
